/**
 * Math utilities for technical indicators
 */

// Base crossover/crossunder functions that work with any numeric values
export const crossoverBase = <T>(valueA1: T, valueA2: T, valueB1: T, valueB2: T): boolean => {
    return valueA1 < valueB1 && valueA2 > valueB2;
}

export const crossunderBase = <T>(valueA1: T, valueA2: T, valueB1: T, valueB2: T): boolean => {
    return valueA1 > valueB1 && valueA2 < valueB2;
}

/**
 * Calculates the average value of a numeric field or array
 */
export const average = <T>(data: T[] | number[], field?: keyof T): number => {
    if (data.length === 0) {
        return 0;
    }

    // If we're dealing with an array of numbers
    if (typeof data[0] === 'number') {
        const numArray = data as number[];
        return numArray.reduce((sum, value) => sum + value, 0) / numArray.length;
    }

    // Otherwise, we're dealing with an array of objects
    if (!field) {
        throw new Error('Field must be provided when calculating average of objects');
    }

    const objArray = data as T[];
    return objArray.reduce((sum, item) => sum + (item[field] as any as number), 0) / objArray.length;
};

/**
 * Calculates the sum of a numeric field
 */
export const sum = <T>(data: T[], field: keyof T): number => {
    return data.reduce((sum, item) => sum + (item[field] as any as number), 0);
}

/**
 * Calculates standard deviation
 */
export const standardDeviation = <T>(data: T[] | number[], field?: keyof T): number => {
    const avg = average(data, field);
    const values = typeof data[0] === 'number' 
        ? data as number[]
        : (data as T[]).map(item => item[field!] as any as number);
    
    const squaredDiffs = values.map(value => Math.pow(value - avg, 2));
    const avgSquaredDiff = squaredDiffs.reduce((sum, value) => sum + value, 0) / values.length;
    
    return Math.sqrt(avgSquaredDiff);
}

/**
 * Calculates the maximum value
 */
export const max = <T>(data: T[] | number[], field?: keyof T): number => {
    if (data.length === 0) return 0;
    
    if (typeof data[0] === 'number') {
        return Math.max(...(data as number[]));
    }
    
    if (!field) {
        throw new Error('Field must be provided when calculating max of objects');
    }
    
    return Math.max(...(data as T[]).map(item => item[field] as any as number));
}

/**
 * Calculates the minimum value
 */
export const min = <T>(data: T[] | number[], field?: keyof T): number => {
    if (data.length === 0) return 0;
    
    if (typeof data[0] === 'number') {
        return Math.min(...(data as number[]));
    }
    
    if (!field) {
        throw new Error('Field must be provided when calculating min of objects');
    }
    
    return Math.min(...(data as T[]).map(item => item[field] as any as number));
}